import type { EventPayload, VideoEventName, VideoEventParams } from "./types";

import { track } from "./analytics";

/**
 * Get common video parameters from the video player element
 */
function getVideoParams(
  player: HTMLVideoElement | null,
): Partial<VideoEventParams> {
  if (!player) {
    return {};
  }

  // Get video resolution (intrinsic dimensions of the video file)
  const resolution =
    player.videoWidth && player.videoHeight
      ? `${player.videoWidth}x${player.videoHeight}`
      : undefined;

  return {
    full_screen: document.fullscreenElement === player,
    position: Math.round(player.currentTime),
    resolution, // e.g., "1920x1080"
    sound: Math.round(player.volume * 100),
    total_length: Math.round(player.duration) || undefined,
  };
}

/**
 * Generate or retrieve a video playback session ID from sessionStorage.
 * This represents a single viewing session and expires when the browser tab closes.
 *
 * @see https://segment.com/docs/connections/spec/video/#playback-event-object
 */
function getVideoSessionId(): string {
  const SESSION_KEY = "video_playback_session_id";

  // Check if we already have a session ID for this browser session
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    // Generate a new session ID for this viewing session
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Track a video event following Segment's Video Playback Spec.
 *
 * @param eventName - Standard Segment video event name
 * @param player - HTML video player element
 * @param additionalProps - Additional tracking properties (session_id is required)
 *
 * @see https://segment.com/docs/connections/spec/video/
 */
function trackVideoEvent(
  eventName: VideoEventName,
  player: HTMLVideoElement | null,
  additionalProps?: Partial<VideoEventParams>,
) {
  const baseProps: Partial<VideoEventParams> = {
    content_asset_id: additionalProps?.content_asset_id,
    content_pod_id: additionalProps?.content_pod_id,
    // Note: Uses the video-specific session ID, not analytics session ID
    session_id: additionalProps?.session_id || getVideoSessionId(),
    video_player: additionalProps?.video_player,
    ...getVideoParams(player),
  };

  const properties = {
    ...baseProps,
    ...additionalProps,
  };

  return track(eventName, properties as EventPayload);
}

export { trackVideoEvent };
